import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { stayExtensionText, StayExtensionText } from '../i18n/pages/stayExtension';
import {
  StayExtensionDeclineReason,
  StayExtensionManagerRequest,
  approveStayExtension,
  declineStayExtension,
  getPendingStayExtensions,
  isArrival,
  isLuggage,
} from '../api.stayExtension';
import touristIcon from '../assets/tourist.png';
import bucketIcon from '../assets/bucket.png';
import checkInIcon from '../assets/check-in.png';
import checkOutIcon from '../assets/check-out.png';
import './StayExtensionRequests.css';

/**
 * The manager's queue, built for someone standing up holding a phone.
 *
 * <p>The decision reads in the order it is actually made — <em>can I?</em>, then <em>why?</em>,
 * then <em>who and how much?</em>, then the buttons. An earlier version printed four facts and
 * left the manager to combine them ("leaves 1h00" eleven words from "you allow 2h00"), which is
 * fine at a desk and does not happen one-handed.
 *
 * <p>The bar is the "why", and it never vetoes: every card stays approvable, because the manager
 * may know something the arithmetic does not — that the cleaner is already in the building, that
 * this flat takes forty minutes, that the arriving guest has texted to say they are late.
 */
const StayExtensionRequests: React.FC = () => {
  const { lang } = useLang();
  const t = stayExtensionText[lang];

  const [rows, setRows] = useState<StayExtensionManagerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getPendingStayExtensions());
    } catch {
      setError(t.actionError);
    } finally {
      setLoading(false);
    }
  }, [t.actionError]);

  useEffect(() => { void load(); }, [load]);

  const act = async (id: number, run: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await run();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-container">
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>{t.pendingTitle}</h1>
      <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 18px' }}>{t.pendingIntro}</p>

      {/* The colours mean the same thing on every card, so the key is page furniture. Repeating it
          per card pushed the actual decisions further down the screen with each request. */}
      {rows.length > 0 && (
        <div className="se-legend">
          <span><i style={{ background: '#f0bfb7', border: '1px solid #a8402f' }} /> {t.legendThisStay}</span>
          <span><i style={{ background: '#fde68a', border: '1px dashed #b45309' }} /> {t.legendAsked}</span>
          <span><i style={{ background: '#fbe7e3', border: '1px solid #dcaaa1' }} /> {t.legendNeighbour}</span>
          <span><i style={{ background: '#e8ecf1', border: '1px solid #c3ccd6' }} /> {t.legendCleaning}</span>
          <span><i style={{ background: '#fff', border: '1px dashed #cbd5e1' }} /> {t.legendFolded}</span>
          <span>
            <i style={{
              background: 'repeating-linear-gradient(45deg,#e8ecf1 0 4px,#e8a79c 4px 8px)',
              border: '1px solid #b91c1c',
            }} /> {t.legendClash}
          </span>
        </div>
      )}

      {error && <div className="se-error">{error}</div>}
      {loading && <div className="se-muted">…</div>}
      {!loading && rows.length === 0 && <div className="se-muted">{t.pendingEmpty}</div>}

      {rows.map(row => (
        <RequestCard
          key={row.id}
          row={row}
          t={t}
          lang={lang}
          busy={busyId === row.id}
          onApprove={(time, price) => act(row.id, () =>
            approveStayExtension(row.id, time ? { approvedTime: time, price } : undefined))}
          onDecline={(reason, closeAll) => act(row.id, () => declineStayExtension(row.id, reason, closeAll))}
        />
      ))}
    </div>
  );
};

// ── Time helpers ───────────────────────────────────────────────────────────

const toMin = (hhmm: string | null): number | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};
const fmt = (m: number) =>
  `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const dur = (m: number) => {
  const h = Math.floor(m / 60), r = m % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h${String(r).padStart(2, '0')}`;
};

// ── Verdict ────────────────────────────────────────────────────────────────

type Tone = 'ok' | 'warn' | 'bad';
interface Verdict { tone: Tone; mark: string; name: string; det: string }

const fill = (s: string, v: Record<string, string>) =>
  Object.entries(v).reduce((a, [k, x]) => a.replace(`{${k}}`, x), s);

/** What this row is asking for. Naming the wrong product would send the manager the wrong answer. */
const askTitle = (type: StayExtensionManagerRequest['type'], t: StayExtensionText) => {
  switch (type) {
    case 'EARLY_CHECKIN': return t.earlyTitle;
    case 'LATE_CHECKOUT': return t.lateTitle;
    case 'LUGGAGE_DROPOFF': return t.luggageDropoffTitle;
    case 'LUGGAGE_PICKUP': return t.luggagePickupTitle;
  }
};

/**
 * Priority matters: a contended window is reported ahead of a tight one, because hours another
 * guest already holds are not the manager's to give whatever the cleaning maths says.
 */
function verdictOf(row: StayExtensionManagerRequest, t: StayExtensionText): Verdict {
  // Bags are not weighed against anything. Falling through would reach "fits — nobody arriving",
  // which is a claim about the turnover that this row has not checked and may well be false.
  if (isLuggage(row.type)) {
    return { tone: 'ok', mark: '✓', name: t.vFits, det: t.vLuggageNoClash };
  }
  if (row.neighbourHolds) {
    return { tone: 'bad', mark: '✕', name: t.vConflict, det: t.vOtherGuestHolds };
  }
  const left = row.minutesForCleaning;
  const need = row.requiredCleaningMinutes;
  if (typeof left === 'number' && typeof need === 'number') {
    const slack = left - need;
    if (slack < 0) return { tone: 'bad', mark: '✕', name: t.vWontFit, det: fill(t.vShort, { time: dur(-slack) }) };
    if (slack < 60) return { tone: 'warn', mark: '⚠', name: t.vTight, det: fill(t.vSpare, { time: dur(slack) }) };
    if (row.blockedBy === 'NOTICE') {
      return { tone: 'warn', mark: '⚠', name: t.vShortNotice, det: fill(t.vNoticeDaysShort, { days: String(row.noticeDays ?? 0) }) };
    }
    return { tone: 'ok', mark: '✓', name: t.vFits, det: fill(t.vSpare, { time: dur(slack) }) };
  }
  if (row.blockedBy === 'NOTICE') {
    return { tone: 'warn', mark: '⚠', name: t.vShortNotice, det: fill(t.vNoticeDaysShort, { days: String(row.noticeDays ?? 0) }) };
  }
  return { tone: 'ok', mark: '✓', name: t.vFits, det: t.vNobodyArriving };
}

// ── The turnover bar ───────────────────────────────────────────────────────

type SegKind = 'booked' | 'asked' | 'clean' | 'clash' | 'fold';
interface Seg {
  kind: SegKind; a: number; b: number;
  icon?: 'tourist' | 'bucket'; mins?: number;
  dir?: 'left' | 'right'; neighbour?: boolean;
  /** Side the asked-for hours abut, so the stay can drop that border and leave one dashed seam. */
  joins?: 'left' | 'right';
}
/**
 * `anchor` decides how a label survives a narrow screen. The asked hour stays pinned to its own
 * position because that is the number being decided; the two outer ones move to the edges, since
 * the bar has already shown where they sit and repeating that under it is what overflowed.
 */
interface Mark { at: number; text: string; color: string; anchor: 'time' | 'edge' }
interface Bar { pos: (m: number) => number; segs: Seg[]; marks: Mark[]; ticks: { m: number; major: boolean }[] }

const OWN_RED = '#a8402f', NEIGH_RED = '#c9948b', ASK_AMBER = '#b45309';

/** An empty run at least this long is worth folding; below it the marker costs more than it wins. */
const FOLD_MIN = 90;
/** The width a folded run is drawn at, whatever its real length. */
const FOLD_SHARE = 0.11;

const floorTo = (m: number) => Math.floor(m / 30) * 30;
const ceilTo = (m: number) => Math.ceil(m / 30) * 30;

/**
 * Lays the turnover out on one window of real minutes, with everything positioned inside it.
 *
 * <p>The neighbouring stay's own times are never sent to the browser — they are derived from
 * {@code minutesForCleaning}, which is the gap this request would leave. The picture is accurate
 * without the API having to describe one guest's booking while talking about another's.
 */
function buildBar(row: StayExtensionManagerRequest, t: StayExtensionText): Bar | null {
  // Nothing to draw for luggage. Every part of this picture — the cleaning block, the neighbouring
  // stay, the clash — exists to show what the request does to the turnover, and a suitcase does
  // nothing to it. A bar with a zero-width cleaning block and no neighbour would be a diagram of
  // an argument nobody is having.
  if (isLuggage(row.type)) return null;

  const std = toMin(row.standardTime);
  const ask = toMin(row.approvedTime || row.requestedTime);
  if (std === null || ask === null) return null;

  const isLate = !isArrival(row.type);
  const need = typeof row.requiredCleaningMinutes === 'number' && Number.isFinite(row.requiredCleaningMinutes)
    ? row.requiredCleaningMinutes : 120;
  const hasN = typeof row.minutesForCleaning === 'number' && Number.isFinite(row.minutesForCleaning);
  const gap = hasN ? (row.minutesForCleaning as number) : null;

  let cleanStart: number, cleanEnd: number, nb: number | null = null;
  if (isLate) {
    cleanStart = ask; cleanEnd = ask + need;
    if (gap !== null) nb = ask + gap;
  } else if (gap !== null) {
    nb = ask - gap; cleanStart = nb; cleanEnd = nb + need;
  } else {
    cleanEnd = ask; cleanStart = ask - need;
  }

  // Cleaning running past the next arrival is the one case that still needs the neighbouring block:
  // the overrun is the entire reason the manager is looking, and it needs somewhere to be seen.
  const clash = isLate && nb !== null && cleanEnd > nb;

  // Otherwise the neighbouring stay is not drawn at all — the bar simply ends at it. That block only
  // ever said "someone else is here", which the edge label underneath already said, and it was
  // spending a tenth of the width to say it twice. The guest's own side keeps a half-hour of padding,
  // because their stay genuinely continues past the edge.
  const events = [std, ask, cleanStart, cleanEnd];
  let lo = floorTo(Math.min(...events) - 30);
  let hi = ceilTo(Math.max(...events) + 30);
  if (nb !== null) {
    if (!isLate) lo = nb;
    else if (clash) hi = ceilTo(Math.max(cleanEnd, nb) + 30);
    else hi = nb;
  }
  // A very short turnover would zoom to an absurd scale. Three hours keeps bars roughly comparable,
  // and the slack always goes on the guest's own side so the neighbour's edge stays where it is.
  while (hi - lo < 180) { if (isLate) lo -= 30; else hi += 30; }

  // The one stretch where nothing at all is scheduled. Drawing it to scale spent up to half the bar
  // on hours with nothing in them; folding it to a sliver that prints its own duration is both
  // narrower and more precise than the empty track it replaces.
  let fold: { a: number; b: number } | null = null;
  if (!clash) {
    const a = cleanEnd;
    const b = isLate ? (nb !== null ? nb : hi) : ask;
    if (b - a >= FOLD_MIN) fold = { a, b };
  }

  const span = hi - lo;
  const folded = fold ? fold.b - fold.a : 0;
  const rest = span - folded;
  if (!Number.isFinite(span) || span <= 0 || rest <= 0) return null;

  // Piecewise across the fold, linear everywhere else. Segments, ticks and marks all position
  // themselves through this, so nothing else has to know the scale has a seam in it.
  const fd = fold;
  const head = fd ? ((fd.a - lo) / rest) * (1 - FOLD_SHARE) : 0;
  const pos = fd
    ? (m: number) => {
      if (m <= fd.a) return ((m - lo) / rest) * (1 - FOLD_SHARE) * 100;
      if (m >= fd.b) return (head + FOLD_SHARE + ((m - fd.b) / rest) * (1 - FOLD_SHARE)) * 100;
      return (head + ((m - fd.a) / folded) * FOLD_SHARE) * 100;
    }
    : (m: number) => ((m - lo) / span) * 100;
  const segs: Seg[] = [];

  // `joins` marks the side where the asked-for hours butt up against this guest's own stay. That
  // border is dropped so the dashed edge of the ask is the only line between them — otherwise a
  // solid rule and a dash sit flush against each other and the seam reads as neither.
  // The fold goes down first so a block can never end up underneath it.
  if (fold) segs.push({ kind: 'fold', a: fold.a, b: fold.b, mins: folded });
  if (isLate) {
    segs.push({ kind: 'booked', a: lo, b: std, icon: 'tourist', joins: 'right' });
    segs.push({ kind: 'asked', a: std, b: ask, dir: 'right' });
    segs.push({ kind: 'clean', a: cleanStart, b: cleanEnd, icon: 'bucket', mins: need });
    if (clash && nb !== null) {
      segs.push({ kind: 'booked', a: nb, b: hi, icon: 'tourist', neighbour: true });
      segs.push({ kind: 'clash', a: nb, b: cleanEnd });
    }
  } else {
    segs.push({ kind: 'clean', a: cleanStart, b: cleanEnd, icon: 'bucket', mins: need });
    segs.push({ kind: 'asked', a: ask, b: std, dir: 'left' });
    segs.push({ kind: 'booked', a: std, b: hi, icon: 'tourist', joins: 'left' });
    if (cleanEnd > ask) segs.push({ kind: 'clash', a: ask, b: cleanEnd });
  }

  // Callouts follow the same hierarchy as the blocks: the neighbour's time is muted, because it
  // is the constraint rather than the subject.
  const marks: Mark[] = [
    { at: ask, text: fill(t.barAskedAt, { time: fmt(ask) }), color: ASK_AMBER, anchor: 'time' },
    {
      at: std, color: OWN_RED, anchor: 'edge',
      text: fill(isLate ? t.barOut : t.barNormal, { time: fmt(std) }),
    },
  ];
  if (nb !== null) {
    marks.push({
      at: nb, color: NEIGH_RED, anchor: 'edge',
      text: fill(isLate ? t.barNextIn : t.barPrevOut, { time: fmt(nb) }),
    });
  }

  // Hours inside a fold are dropped rather than drawn: the scale has no position to give them, and
  // a tick there would claim one. The window no longer starts on the hour, so the first tick is
  // sought rather than assumed.
  const ticks: { m: number; major: boolean }[] = [];
  for (let m = Math.ceil(lo / 60) * 60; m <= hi; m += 60) {
    if (fold && m > fold.a && m < fold.b) continue;
    ticks.push({ m, major: (m / 60) % 2 === 0 });
  }

  return { pos, segs, marks, ticks };
}

// ── Card ───────────────────────────────────────────────────────────────────

const RequestCard: React.FC<{
  row: StayExtensionManagerRequest;
  t: StayExtensionText;
  lang: string;
  busy: boolean;
  onApprove: (time?: string, price?: number) => void;
  onDecline: (reason: StayExtensionDeclineReason, closeAll: boolean) => void;
}> = ({ row, t, lang, busy, onApprove, onDecline }) => {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState<StayExtensionDeclineReason | null>(null);
  const [closeAll, setCloseAll] = useState(false);
  const [countering, setCountering] = useState(false);
  const [counterTime, setCounterTime] = useState('');
  const [counterPrice, setCounterPrice] = useState('');
  const verdict = verdictOf(row, t);
  const bar = buildBar(row, t);
  const isLate = !isArrival(row.type);
  const money = row.price === null || row.price <= 0
    ? t.free
    : `${row.price.toLocaleString(lang === 'en' ? 'en-GB' : lang)} CZK`;

  // When it fits, approving is the obvious move and gets the accent. When it does not, the
  // counter-offer is promoted instead: "I can't do 15:00 but I can do 12:00" is the answer that
  // keeps the sale, so it should be the easy tap rather than the one behind a prompt.
  const approveIsPrimary = verdict.tone !== 'bad';

  // A counter-offer is two decisions, not one: which hour, and what to charge for it. The old
  // window.prompt() asked only the first and silently applied the stair price to the second, so a
  // manager trading an hour for a discount had no way to say so. The stair is listed because it is
  // the context the price is chosen against — nobody remembers what they priced 12:00 at.
  const startCounter = () => {
    const seed = row.counterOptions.find(o => o.time === row.requestedTime)
      ?? row.counterOptions[0];
    setCounterTime(seed?.time ?? row.requestedTime);
    setCounterPrice(seed ? String(seed.price) : '');
    setCountering(true);
  };

  /** Picking an hour re-quotes it. The price stays editable — that is the point of the panel. */
  const pickCounterTime = (time: string, price: number) => {
    setCounterTime(time);
    setCounterPrice(String(price));
  };

  const counterPriceNum = Number(counterPrice);
  const counterValid = counterTime !== ''
    && counterPrice.trim() !== ''
    && Number.isFinite(counterPriceNum)
    && counterPriceNum >= 0;

  // The guest is shown this, so it is picked from a fixed set rather than typed. Opening a chooser
  // instead of declining on the first tap also makes the terminal action deliberate.
  // Two of these are about the apartment being occupied, which is never why bags are refused —
  // offering them for luggage would invite a host to send a guest an explanation that is not true.
  const REASONS: { code: StayExtensionDeclineReason; label: string; guest: string }[] = isLuggage(row.type)
    ? [
      { code: 'NO_SPACE', label: t.mNoSpace, guest: t.dNoSpace },
      { code: 'SHORT_NOTICE', label: t.mShortNotice, guest: t.dShortNotice },
      { code: 'UNSPECIFIED', label: t.mUnspecified, guest: t.dUnspecified },
    ]
    : [
      { code: 'CLEANING', label: t.mCleaning, guest: t.dCleaning },
      { code: 'OTHER_BOOKING', label: t.mOtherBooking, guest: t.dOtherBooking },
      { code: 'SHORT_NOTICE', label: t.mShortNotice, guest: t.dShortNotice },
      { code: 'UNSPECIFIED', label: t.mUnspecified, guest: t.dUnspecified },
    ];

  return (
    <div className="se-card">
      <div className={`se-verdict ${verdict.tone}`}>
        <span className="mark">{verdict.mark}</span>
        <span className="name">{verdict.name}</span>
        <span className="det">· {verdict.det}</span>
      </div>

      <div className="se-body">
        <div className="se-head">
          <span className="se-type">
            {isLuggage(row.type)
              ? <span style={{ fontSize: 22, lineHeight: '26px' }} aria-hidden="true">🧳</span>
              : <img src={isLate ? checkOutIcon : checkInIcon} alt="" width={26} height={26} />}
          </span>
          <span className="se-who">
            <Link to={`/reservations/${row.reservationId}`} className="se-name">{row.guestName}</Link>
            <div className="se-meta-line">
              {row.apartmentName} · {row.checkInDate} → {row.checkOutDate}
            </div>
          </span>
        </div>

        <div className="se-ask">
          {askTitle(row.type, t)}{' '}
          <span className="std">{row.standardTime}</span>
          {' → '}
          <span className="to">{row.requestedTime}</span>
        </div>

        {bar && (
          <div className="se-bar">
            <div className="se-axis">
              {bar.ticks.map(tick => (
                <React.Fragment key={tick.m}>
                  <span className={`tk${tick.major ? '' : ' minor'}`} style={{ left: `${bar.pos(tick.m)}%` }} />
                  {tick.major && (
                    <span className="lb" style={{ left: `${bar.pos(tick.m)}%` }}>{fmt(tick.m)}</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div
              className="se-track"
              role="img"
              aria-label={`${verdict.name} — ${verdict.det}`}
            >
              {bar.segs.map((seg, i) => {
                const left = bar.pos(seg.a);
                const width = bar.pos(seg.b) - bar.pos(seg.a);
                if (width <= 0) return null;
                const cls = [
                  'se-seg', seg.kind,
                  seg.dir ?? '',
                  seg.neighbour ? 'neighbour' : '',
                  seg.joins ? `joins-${seg.joins}` : '',
                ].filter(Boolean).join(' ');
                return (
                  <div key={i} className={cls} style={{ left: `${left}%`, width: `${width}%` }}>
                    {seg.icon === 'tourist' && (
                      <span className="lab">
                        <span className="se-ico tourist" style={maskStyle(touristIcon)} />
                      </span>
                    )}
                    {seg.icon === 'bucket' && (
                      <span className="lab">
                        <span className="se-ico bucket" style={maskStyle(bucketIcon)} />
                        {width > 14 && seg.mins !== undefined ? dur(seg.mins) : ''}
                      </span>
                    )}
                    {/* The broken-axis double slash, and beside it how much time was folded away.
                        The number is what keeps the compression honest — without it a three-hour
                        gap would read as twenty minutes. Below ~30px it will not fit, and the bare
                        slashes stand alone; that is also the case where the exact length matters
                        least. */}
                    {seg.kind === 'fold' && (
                      <span className="lab">
                        <svg width="13" height="15" viewBox="0 0 13 15" aria-hidden="true">
                          <path
                            d="M3 12.5 L6.5 2.5 M7 12.5 L10.5 2.5"
                            stroke="currentColor" strokeWidth="1.5"
                            fill="none" strokeLinecap="round"
                          />
                        </svg>
                        {width > 9 && seg.mins !== undefined ? dur(seg.mins) : ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* The two outer labels park at the ends of the row rather than under their own hour;
                the bar has already shown where those sit, and printing three times at three
                positions is what ran off a narrow screen. Which end is read from the time itself,
                not from array order, so a card with no neighbouring stay still lands correctly.
                The asked hour keeps its true position, because it is the number being decided. */}
            <div className="se-marks">
              {bar.marks.map((m, i) => {
                const p = bar.pos(m.at);
                if (m.anchor === 'edge') {
                  return (
                    <span
                      key={i} className={`m ${p < 50 ? 'lft' : 'rgt'}`}
                      style={{ color: m.color }}
                    >
                      {m.text}
                    </span>
                  );
                }
                // Clamped so a request sitting at the very edge of its window cannot push the chip
                // off the card. With a tight window the asked hour lands mid-bar and this never
                // fires; it is here for the odd shape, not the normal one.
                return (
                  <span
                    key={i} className="m mid"
                    style={{ left: `${Math.min(90, Math.max(10, p))}%`, color: m.color }}
                  >
                    {m.text}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className="se-meta">
          <b>{money}</b>
          {' · '}{fill(t.vNoticeDaysShort, { days: String(row.noticeDays ?? 0) })}
          {row.guestNote && <> · <em>“{row.guestNote}”</em></>}
        </div>

        {/* The manager can still approve — they may be about to connect the account, or may take
            the money another way. But it is said before the buttons rather than discovered by the
            guest hitting "payment is temporarily unavailable" with nobody told why. */}
        {!row.paymentsReady && row.price !== null && row.price > 0 && (
          <div className="se-nopay">
            <span aria-hidden="true">⚠️</span>
            <span>
              {t.noPaymentAccount}{' '}
              <Link to={`/reservations/${row.reservationId}`}>{t.noPaymentAccountFix}</Link>
            </span>
          </div>
        )}

        <div className="se-acts">
          <button
            type="button" disabled={busy} onClick={() => onApprove()}
            className={`se-btn${approveIsPrimary ? ' primary' : ''}`}
          >
            {approveIsPrimary ? fill(t.approveCta, { time: row.requestedTime }) : t.approveAnyway}
          </button>
          <div className="row">
            <button
              type="button" disabled={busy || row.counterOptions.length === 0}
              onClick={() => (countering ? setCountering(false) : startCounter())}
              className={`se-btn${approveIsPrimary ? '' : ' primary'}`}
            >
              {t.counterCta}
            </button>
            <button
              type="button" disabled={busy} onClick={() => setDeclining(d => !d)}
              className="se-btn quiet"
            >
              {t.declineCta}
            </button>
          </div>
        </div>

        {countering && (
          <div className="se-counter">
            <div className="se-decline-title">{t.counterTitle}</div>

            {/* The whole stair, with the hour the guest picked marked. Reading it back is the
                point: the manager set these prices once, months ago, and is now being asked to
                name one from memory while a guest waits. */}
            <div className="se-stair">
              {row.counterOptions.map(o => (
                <button
                  key={o.time} type="button" disabled={busy}
                  className={`se-step${counterTime === o.time ? ' sel' : ''}`}
                  onClick={() => pickCounterTime(o.time, o.price)}
                >
                  <span className="tm">{o.time}</span>
                  <span className="pr">
                    {o.price <= 0 ? t.free : `${o.price.toLocaleString(lang === 'en' ? 'en-GB' : lang)} CZK`}
                  </span>
                  {o.time === row.requestedTime && <span className="tagAsked">{t.counterAsked}</span>}
                </button>
              ))}
            </div>

            {/* Seeded from the stair but editable, so an hour can be traded for a discount — the
                usual shape of "not 15:00, but I'll do 13:00 for less". */}
            <label className="se-price">
              <span className="lab">{t.counterPriceLabel}</span>
              <span className="fld">
                <input
                  type="number" min="0" step="1" inputMode="numeric"
                  value={counterPrice} disabled={busy}
                  onChange={e => setCounterPrice(e.target.value)}
                />
                <span className="cur">CZK</span>
              </span>
            </label>

            <div className="se-decline-acts">
              <button type="button" className="se-btn" onClick={() => setCountering(false)}>
                {t.declineCancel}
              </button>
              <button
                type="button" className="se-btn primary" disabled={busy || !counterValid}
                onClick={() => onApprove(counterTime, counterPriceNum)}
              >
                {fill(t.counterConfirm, { time: counterTime })}
              </button>
            </div>
          </div>
        )}

        {declining && (
          <div className="se-decline">
            <div className="se-decline-title">{t.declineTitle}</div>
            {REASONS.map(r => (
              <button
                key={r.code} type="button" disabled={busy}
                className={`se-reason${reason === r.code ? ' sel' : ''}`}
                onClick={() => {
                  setReason(r.code);
                  // Short notice is about when they asked, not which hour, so it closes every hour
                  // by definition — a manager choosing it has answered the scope question already.
                  // Every other reason defaults to the option that cannot lose a sale.
                  setCloseAll(r.code === 'SHORT_NOTICE');
                }}
              >
                <span className="lab">{r.label}</span>
                <span className="guest">{fill(t.declineGuestSees, { text: r.guest })}</span>
              </button>
            ))}

            {reason && (
              <button
                type="button" disabled={busy}
                className={`se-scope${closeAll ? ' on' : ''}`}
                onClick={() => setCloseAll(c => !c)}
                aria-pressed={closeAll}
              >
                <span className="cb" aria-hidden="true">{closeAll ? '✓' : ''}</span>
                <span>
                  <span className="lab">
                    {isLate ? t.scopeLabelLate : t.scopeLabelEarly}
                    {reason === 'SHORT_NOTICE' && closeAll && (
                      <span className="auto">{t.scopeAuto}</span>
                    )}
                  </span>
                  <span className="hint">
                    {reason === 'SHORT_NOTICE'
                      ? t.scopeHintNotice
                      : fill(isLate ? t.scopeHintLate : t.scopeHintEarly, { time: row.requestedTime })}
                  </span>
                </span>
              </button>
            )}

            <div className="se-decline-acts">
              <button type="button" className="se-btn" onClick={() => setDeclining(false)}>
                {t.declineCancel}
              </button>
              <button
                type="button" className="se-btn danger"
                disabled={busy || !reason}
                onClick={() => reason && onDecline(reason, closeAll)}
              >
                {t.declineConfirm}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Only the URL comes from JS — webpack hashes the import, so it cannot live in the stylesheet. */
const maskStyle = (url: string): React.CSSProperties =>
  ({ WebkitMaskImage: `url(${url})`, maskImage: `url(${url})` } as React.CSSProperties);

export default StayExtensionRequests;
